import React from "react";
import { connect } from "react-redux";

import OrderService from "services/order";
import OrderItemService from "services/order-item";

import {
  update,
  create,
  remove,
  updateItem,
  createItem,
  removeItem,
} from "reducers/order";
import { setBreadcrumbs } from "reducers/view";

import ZeeForm from "components/ZeeForm";
import ZeeTable from "components/ZeeTable";

import Button from "@material-ui/core/Button";

class CreateOrder extends React.Component {
  orderService = new OrderService();
  orderItemService = new OrderItemService();

  form = {
    pages: [
      {
        inputs: [
          {
            name: "Date ordered",
            type: "Date",
            value: "date",
          },
          {
            name: "Customer",
            type: "Select",
            value: "customer_id",
            items: [{ name: "Please select" }, ...this.customers],
          },
          {
            name: "Customer reference",
            type: "Textbox",
            value: "customer_reference",
          },
        ],
      },
    ],
  };

  state = {
    customer_id: this.currentItem?.customer_id ?? null,
    customer_reference: this.currentItem?.customer_reference ?? null,
    date: this.currentItem?.date ?? Math.round(Date.now() / 1000),
    items: this.currentItems ?? [],
  };

  columns = {
    quantity: {
      name: "Quantity",
    },
    product_id: {
      name: "Product",
      values: [{ name: "Please select" }, ...this.products],
      format: (productId, row) => this.getProductById(productId)?.name,
    },
    price: {
      name: "Price",
      format: (price, row) => this.getPrice(row),
    },
    discount: { name: "Discount %" },
    subtotal: {
      name: "Subtotal",
      noEdit: true,
      format: (subtotal, row) => this.getSubtotal(row),
    },
  };

  constructor() {
    super(...arguments);
    if (this.isEditing) {
      this.props.setBreadcrumbs([
        { name: "Orders", slug: "/orders" },
        { name: "Editing Order", slug: `/orders/${this.currentId}` },
      ]);
    } else {
      this.props.setBreadcrumbs([
        { name: "Orders", slug: "/orders" },
        { name: "Create Order", slug: "/orders/create" },
      ]);
    }
  }

  getSubtotal(row) {
    const discount = row.discount > 0 ? row.discount / 100 : null;
    const price = this.getPrice(row);
    const quantity = row.quantity > 0 ? row.quantity : 1;

    if (!price) {
      return 0;
    }

    const total = price * quantity;

    if (discount) {
      return (total - total * discount).toFixed(2);
    }

    return total.toFixed(2);
  }

  getPrice(row) {
    const price =
      row.price === null || row.price === ""
        ? this.getProductById(row.product_id)?.price
        : row.price;

    return price;
  }

  get currentId() {
    return this.props.match.params.id ?? null;
  }

  get currentItem() {
    return this.props.orders.find((item) => item.id === this.currentId);
  }

  get currentItems() {
    return this.props.orderItem.filter(
      (item) => item.orders_id === this.currentId
    );
  }

  get isEditing() {
    return this.currentId ? true : false;
  }

  get items() {
    return this.state.items;
  }

  get customers() {
    return this.props.customer?.map(({ id, name }) => ({ id, name })) ?? [];
  }

  get products() {
    return this.props.product?.map(({ id, name }) => ({ id, name })) ?? [];
  }

  getProductById(productId) {
    return this.props.product?.find(
      (item) => parseInt(item.id) === parseInt(productId)
    );
  }

  getItemById(itemId) {
    return this.items?.find((item) => parseInt(item.id) === parseInt(itemId));
  }

  onEdit(row) {
    if (!row.product_id) {
      return false;
    }

    const existing = this.getItemById(row.id);

    row.quantity = row.quantity > 0 ? row.quantity : 1;
    row.discount = row.discount > 0 ? row.discount : 0;

    row.price = this.getPrice(row);
    if (parseInt(existing.product_id) !== parseInt(row.product_id)) {
      row.price = this.getPrice({ ...row, price: null });
    }

    const items = this.items.map((item) =>
      item.id === row.id ? { ...item, ...row } : item
    );

    this.setState({ items });

    return true;
  }

  onDelete(row) {
    const items = this.items.reduce(
      (agg, item) => (item.id === row.id ? agg : [...agg, item]),
      []
    );

    this.setState({ items });
  }

  onAdd(row) {
    if (!row.product_id) {
      return false;
    }

    row.isNew = true;
    row.quantity = row.quantity > 0 ? row.quantity : 1;
    row.discount = row.discount > 0 ? row.discount : 0;
    row.price = this.getPrice(row);

    this.setState({
      items: [...this.items, row],
    });

    return true;
  }

  onUpdate(data) {
    this.setState({
      ...data,
    });
  }

  async onConfirm() {
    const { customer_id, date, items, customer_reference } = this.state;

    if (!customer_id || !date) {
      return;
    }

    const order = {
      customer_id,
      customer_reference,
      date,
    };

    if (this.currentId) {
      // Update
      const result = await this.orderService
        .update({ id: this.currentId, ...order })
        .then(({ data }) => data);

      if (result.success) {
        this.props.update(result.data);

        const toDelete = this.currentItems.reduce(
          (agg, item) =>
            items.find((item2) => item.id === item2.id) ? agg : [...agg, item],
          []
        );

        const toUpdate = items.reduce(
          (agg, item) => (item?.isNew ? agg : [...agg, item]),
          []
        );

        const toAdd = items.reduce(
          (agg, item) => (item?.isNew ? [...agg, item] : agg),
          []
        );

        const updateResultItems = await Promise.all(
          toUpdate.map(async (item) =>
            this.orderItemService.update(item).then(({ data }) => data)
          )
        );

        updateResultItems.forEach((item) => this.props.updateItem(item.data));

        const createResultItems = await Promise.all(
          toAdd.map(async (item) =>
            this.orderItemService
              .create({ ...item, orders_id: this.currentId })
              .then(({ data }) => data)
          )
        );

        createResultItems.forEach((item) => this.props.createItem(item.data));

        const deleteResultItems = await Promise.all(
          toDelete.map(async (item) =>
            this.orderItemService.delete(item).then(({ data }) => data)
          )
        );

        deleteResultItems.forEach((item) => this.props.removeItem(item.data));

        return true;
      }

      return false;
    }

    if (!items || items?.length === 0) {
      return null;
    }

    // Create
    const result = await this.orderService
      .create(order)
      .then(({ data }) => data);

    if (result.success) {
      const { id } = result.data;
      this.props.create(result.data);

      const resultItems = await Promise.all(
        items.map(async (item) =>
          this.orderItemService
            .create({ ...item, orders_id: id })
            .then(({ data }) => data)
        )
      );

      resultItems.forEach((item) => this.props.createItem(item.data));

      this.props.history.push(`/orders/${id}`);

      return true;
    }

    return false;
  }

  onCancel() {
    this.props.history.push("/orders");
  }

  render() {
    return (
      <div className="form">
        {this.isEditing ? <h1>Edit order</h1> : <h1>Create a new order</h1>}

        <ZeeForm
          form={this.form}
          data={this.state}
          onUpdate={(data) => this.onUpdate(data)}
        />
        <ZeeTable
          columns={this.columns}
          data={this.items}
          hasAdd={true}
          hasEdit={true}
          hasDelete={true}
          onAdd={(row) => this.onAdd(row)}
          onEdit={(row) => this.onEdit(row)}
          onDelete={(row) => this.onDelete(row)}
        />
        <Button
          variant="contained"
          color="primary"
          onClick={() => this.onConfirm()}
        >
          {this.isEditing ? "Update" : "Create"}
        </Button>
        <Button color="secondary" onClick={() => this.onCancel()}>
          Cancel
        </Button>
      </div>
    );
  }
}

export default connect(
  (state) => {
    return {
      product: state.product.all,
      customer: state.customer.all,
      orders: state.order.all,
      orderItem: state.order.item.all,
    };
  },
  { update, create, remove, updateItem, createItem, removeItem, setBreadcrumbs }
)(CreateOrder);
